// A building's registers (tiles) and the smaller sections under them — Defects, Drawings & documents, Requests &
// follow-ups, Inspections — each collapsible, each can be hidden (remembered on this browser), each links to its tab.
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { supabase } from '../../lib/supabase';
import { fmtDate } from '../../lib/format';
import { TYPE_LOOK, isClosed, registerOfType, statusLabel, type ItemType } from '../../lib/registers';
import { docTypeLabel } from '../../lib/buildings';
import { useViewer } from '../Viewer';

export type BItem = { id: string; type: ItemType; number: string | null; title: string; status: string; amount: number | null; respond_by: string | null; onus_org_id: string | null; review: unknown[] | null };

const TILES: { type: ItemType; label: string }[] = [
  { type: 'rfi', label: 'RFIs & SIs' },
  { type: 'notice', label: 'Notices' },
  { type: 'variation', label: 'Variations & Requests' },
  { type: 'claim', label: 'Claims' },
];

export function RegisterTiles({ buildingId, items, today }: { buildingId: string; items: BItem[]; today: string }) {
  return (
    <section className="card">
      <div className="card-head">
        <h2>Registers for this building</h2>
        <span className="muted small on-dark">Click to open the register filtered to this building</span>
      </div>
      <div className="reg-tiles">
        {TILES.map((t) => {
          const look = TYPE_LOOK[t.type];
          const mine = items.filter((i) => i.type === t.type);
          const open = mine.filter((i) => !isClosed(i.status));
          const late = open.filter((i) => i.respond_by && i.respond_by < today);
          const total = mine.length || 1;
          let line = `${open.length} open · ${mine.length - open.length} closed`;
          if (t.type === 'variation') {
            const appr = mine.filter((i) => i.status === 'approved' || i.status === 'complete');
            line = `${appr.length} approved · ${open.length} open`;
          }
          if (late.length) line += ` · ${late.length} overdue`;
          return (
            <Link key={t.type} to={`/r/${registerOfType(t.type)?.module}?b=${buildingId}`} className="reg-tile" style={{ background: look.tint }}>
              <span className="reg-top">
                <span className="btt" style={{ background: look.color }}>
                  {look.tag}
                </span>
                <b className="reg-n">{mine.length}</b>
              </span>
              <b>{t.label}</b>
              <span className="reg-bar" aria-hidden="true">
                <i style={{ width: `${(open.length / total) * 100}%`, background: look.color }} />
                <i style={{ width: `${(late.length / total) * 100}%`, background: 'var(--orange)' }} />
              </span>
              <span className="small">{line}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

type Sec = 'defects' | 'drawings' | 'requests' | 'inspections';
const SECS: { key: Sec; tag: string; color: string; name: string; link: string | null; linkLabel: string }[] = [
  { key: 'defects', tag: 'DEFECT', color: '#8A2B2B', name: 'Defects', link: null, linkLabel: 'Defects register' },
  { key: 'drawings', tag: 'DWG', color: '#3D3730', name: 'Drawings & documents', link: '/files', linkLabel: 'Open Files' },
  { key: 'requests', tag: 'REQ', color: '#0F6B6B', name: 'Requests & follow-ups', link: null, linkLabel: 'Follow-ups' },
  { key: 'inspections', tag: 'INSP', color: '#6A3D8F', name: 'Inspections', link: null, linkLabel: 'Inspections register' },
];

const prefKey = (uid: string) => `pmc3.bsec.${uid}`;
function readPrefs(uid: string): { hidden: Sec[]; open: Sec[] } {
  try {
    const v = JSON.parse(localStorage.getItem(prefKey(uid)) ?? '');
    return { hidden: Array.isArray(v?.hidden) ? v.hidden : [], open: Array.isArray(v?.open) ? v.open : ['requests'] };
  } catch {
    return { hidden: [], open: ['requests'] };
  }
}

type FU = { id: string; item_id: string; title: string; due_on: string | null; done_at: string | null };
type BDoc = { id: string; original_name: string; mime: string | null; kind: string; doc_type: string | null; created_at: string };

export function MoreSections({ uid, buildingId, items, today, docsVersion }: { uid: string; buildingId: string; items: BItem[]; today: string; docsVersion: number }) {
  const viewer = useViewer();
  const [prefs, setPrefs] = useState(() => readPrefs(uid));
  const [custom, setCustom] = useState(false);
  const [fus, setFus] = useState<FU[]>([]);
  const [docs, setDocs] = useState<BDoc[]>([]);
  const save = (p: typeof prefs) => {
    setPrefs(p);
    try {
      localStorage.setItem(prefKey(uid), JSON.stringify(p));
    } catch {
      /* private window — fine, just not remembered */
    }
  };
  const ids = items.map((i) => i.id);
  useEffect(() => {
    if (!ids.length) return setFus([]);
    void (async () => {
      const out: FU[] = [];
      for (let k = 0; k < ids.length; k += 150) {
        const { data } = await supabase.from('follow_ups').select('id, item_id, title, due_on, done_at').in('item_id', ids.slice(k, k + 150)).is('deleted_at', null).is('done_at', null);
        out.push(...((data ?? []) as FU[]));
      }
      setFus(out.sort((a, b) => (a.due_on ?? '9').localeCompare(b.due_on ?? '9')));
    })();
  }, [ids.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    void supabase
      .from('files')
      .select('id, original_name, mime, kind, doc_type, created_at')
      .eq('linked_table', 'buildings')
      .eq('linked_id', buildingId)
      .is('deleted_at', null)
      .eq('is_current', true)
      .order('created_at', { ascending: false })
      .then(({ data }) => setDocs((data ?? []) as BDoc[]));
  }, [buildingId, docsVersion]);

  const item = (id: string) => items.find((i) => i.id === id);
  const summary: Record<Sec, string> = {
    defects: 'The defects register arrives with the field app — nothing here yet',
    drawings: `${docs.length} building document${docs.length === 1 ? '' : 's'} · drawings register to come`,
    requests: `${fus.length} open follow-up${fus.length === 1 ? '' : 's'}${fus.filter((f) => f.due_on && f.due_on < today).length ? ` · ${fus.filter((f) => f.due_on && f.due_on < today).length} overdue` : ''}`,
    inspections: 'The inspections register arrives with the field app — nothing here yet',
  };

  return (
    <div className="bsecs">
      <div className="resp-row">
        <span className="k grow">More on this building</span>
        <button type="button" className="btn btn-secondary btn-sm" aria-expanded={custom} onClick={() => setCustom(!custom)}>
          {custom ? 'Done' : 'Show / hide sections'}
        </button>
      </div>
      {custom ? (
        <div className="bsec-pick" role="group" aria-label="Sections to show">
          <span className="muted small">Show on the building page:</span>
          {SECS.map((s) => {
            const on = !prefs.hidden.includes(s.key);
            return (
              <button key={s.key} type="button" className={`chip ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => save({ ...prefs, hidden: on ? [...prefs.hidden, s.key] : prefs.hidden.filter((x) => x !== s.key) })}>
                {on ? '✓' : '+'} {s.name}
              </button>
            );
          })}
          <span className="muted small">· remembered on this browser</span>
        </div>
      ) : null}
      {SECS.filter((s) => !prefs.hidden.includes(s.key)).map((s) => {
        const isOpen = prefs.open.includes(s.key);
        return (
          <section key={s.key} className="card bsec">
            <div className="bsec-head">
              <button type="button" className="bsec-btn" aria-expanded={isOpen} onClick={() => save({ ...prefs, open: isOpen ? prefs.open.filter((x) => x !== s.key) : [...prefs.open, s.key] })}>
                <svg className={`bchev ${isOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" />
                </svg>
                <span className="btt" style={{ background: s.color }}>
                  {s.tag}
                </span>
                <b>{s.name}</b>
                <span className="muted small grow">{summary[s.key]}</span>
              </button>
              {s.link ? (
                <Link className="small strong" to={s.link}>
                  {s.linkLabel} →
                </Link>
              ) : (
                <span className="muted small">{s.linkLabel} — coming</span>
              )}
            </div>
            {isOpen ? (
              <div className="bsec-body">
                {s.key === 'requests' ? (
                  fus.length ? (
                    fus.slice(0, 8).map((f) => {
                      const it = item(f.item_id);
                      const late = f.due_on && f.due_on < today;
                      return (
                        <div key={f.id} className="bsec-row">
                          <b className="small">{it?.number ?? ''}</b>
                          <Link to={`/r/${registerOfType(it?.type ?? 'rfi')?.module}/${f.item_id}`} className="grow">
                            {f.title}
                          </Link>
                          <span className={`chip-s ${late ? 'bad' : ''}`}>{f.due_on ? (late ? `Overdue · ${fmtDate(f.due_on)}` : `Due ${fmtDate(f.due_on)}`) : 'No date'}</span>
                        </div>
                      );
                    })
                  ) : (
                    <p className="muted small">No open follow-ups on this building's items.</p>
                  )
                ) : s.key === 'drawings' ? (
                  docs.length ? (
                    docs.slice(0, 8).map((d) => (
                      <div key={d.id} className="bsec-row">
                        <button type="button" className="linkbtn grow" onClick={() => viewer({ id: d.id, name: d.original_name, mime: d.mime, kind: d.kind })}>
                          {d.original_name}
                        </button>
                        <span className="bpill">{docTypeLabel(d.doc_type)}</span>
                        <span className="muted small">{fmtDate(d.created_at)}</span>
                      </div>
                    ))
                  ) : (
                    <p className="muted small">No building documents yet — upload one with "Upload a file — Claude reads it".</p>
                  )
                ) : (
                  <p className="muted small">{summary[s.key]}. Items will show here once the register is live.</p>
                )}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

/** Items on this building with a "To be reviewed" flag. */
export function ReviewList({ items }: { items: BItem[] }) {
  const flagged = items.filter((i) => i.review && i.review.length);
  if (!flagged.length) return null;
  return (
    <section className="card review-card">
      <div className="card-head">
        <h2>To be reviewed · {flagged.length}</h2>
      </div>
      {flagged.slice(0, 6).map((i) => (
        <Link key={i.id} className="bsec-row" to={`/r/${registerOfType(i.type)?.module}/${i.id}`}>
          <span className="btt" style={{ background: TYPE_LOOK[i.type].color }}>
            {TYPE_LOOK[i.type].tag}
          </span>
          <span className="grow">
            {i.number ?? ''} {i.title}
          </span>
          <span className="muted small">{statusLabel(i.status, i.type)}</span>
        </Link>
      ))}
      {flagged.length > 6 ? <p className="muted small" style={{ padding: '6px 16px', margin: 0 }}>+ {flagged.length - 6} more — open a register and use "To be reviewed".</p> : null}
    </section>
  );
}
